// ═══════════════════════════════════════════════════════════════════════════════
// Limit reached modal — common UI when quotas are exceeded (§22)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { LIMIT_ERROR_CODE, USER_LEVEL, USER_ROLE } from '@fdh/shared';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  emitLimitReached,
  parseLimitReachedFromApiClientError,
  registerLimitReachedListener,
  resolveBatchInsufficientBodyKey,
  type LimitReachedPayload,
} from '../lib/limitError';
import { AppButton } from '../components/ui/AppButton';
import { AppModal } from '../components/ui/AppModal';
import type { MessageParams } from '../i18n/uiMessages';
import { canUseStripeBillingUi, isPlayStoreClient } from '../lib/clientChannel';
import { usePublicConfig } from '../hooks/usePublicConfig';

type Translate = (key: string, params?: MessageParams) => string;

const bodyKeyForCode = (code: string): string => `limitModal.body.${code}`;

const titleKeyForCode = (code: string): string => `limitModal.title.${code}`;

const resolveLimitModalBodyLines = (payload: LimitReachedPayload, t: Translate): string[] => {
  const requested = payload.requestedCount ?? 0;

  if (payload.batchIssues && payload.batchIssues.length > 0) {
    return payload.batchIssues.map((issue) => {
      const bodyKey =
        requested > 0
          ? resolveBatchInsufficientBodyKey(issue.code, payload.batchVariant)
          : bodyKeyForCode(issue.code);
      const remaining = Math.max(0, issue.dailyLimit - issue.usedToday);
      const params: MessageParams = {
        used: issue.usedToday,
        limit: issue.dailyLimit,
        remaining,
        requested,
        retryAt: issue.resetAt,
        total: payload.total ?? 0,
        max: payload.maxComments ?? payload.maxPinMessages ?? payload.maxTotalTasks ?? 0,
      };
      const bodyRaw = t(bodyKey, params);
      return bodyRaw === bodyKey ? t('limitModal.body.default') : bodyRaw;
    });
  }

  const dailyLimit = payload.dailyLimit ?? 0;
  const usedToday = payload.usedToday ?? 0;
  const remaining = Math.max(0, dailyLimit - usedToday);
  const bodyKey =
    requested > 0 &&
    (payload.code === LIMIT_ERROR_CODE.DAILY_RECEIPT_LIMIT_REACHED ||
      payload.code === LIMIT_ERROR_CODE.DAILY_AI_ANALYZE_LIMIT_REACHED)
      ? resolveBatchInsufficientBodyKey(payload.code, payload.batchVariant)
      : bodyKeyForCode(payload.code);
  const params: MessageParams = {
    used: usedToday,
    limit: dailyLimit,
    remaining,
    requested,
    retryAt: payload.retryAt ?? '',
    total: payload.total ?? 0,
    max: payload.maxComments ?? payload.maxPinMessages ?? payload.maxTotalTasks ?? 0,
  };
  const bodyRaw = t(bodyKey, params);
  const body = bodyRaw === bodyKey ? t('limitModal.body.default') : bodyRaw;
  return [body];
};

const resolveLimitModalTitle = (payload: LimitReachedPayload, t: Translate): string => {
  if (payload.batchIssues && payload.batchIssues.length > 1) {
    const combinedKey = 'limitModal.title.batchInsufficient';
    const combinedRaw = t(combinedKey);
    if (combinedRaw !== combinedKey) {
      return combinedRaw;
    }
  }
  const code = payload.batchIssues?.[0]?.code ?? payload.code;
  const titleKey = titleKeyForCode(code);
  const titleRaw = t(titleKey);
  return titleRaw === titleKey ? t('limitModal.title.default') : titleRaw;
};

type LimitReachedModalViewProps = {
  payload: LimitReachedPayload;
  isHelper: boolean;
  isPaidEmployer: boolean;
  showPlayStoreOfficialWebsiteUrl: boolean;
  t: Translate;
  onClose: () => void;
  onViewUsage: () => void;
  onViewUsageKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onGoSubscription: () => void;
  onGoSubscriptionKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

const LimitReachedModalView = ({
  payload,
  isHelper,
  isPaidEmployer,
  showPlayStoreOfficialWebsiteUrl,
  t,
  onClose,
  onViewUsage,
  onViewUsageKeyDown,
  onGoSubscription,
  onGoSubscriptionKeyDown,
}: LimitReachedModalViewProps) => {
  const bodyLines = useMemo(() => resolveLimitModalBodyLines(payload, t), [payload, t]);
  const title = useMemo(() => resolveLimitModalTitle(payload, t), [payload, t]);
  const hint = isHelper
    ? t('limitModal.helperHint')
    : isPaidEmployer
      ? t('limitModal.employerL3Hint')
      : payload.code === LIMIT_ERROR_CODE.TASK_KIND_NOT_ALLOWED
        ? t('limitModal.employerTaskKindHint')
        : payload.code === LIMIT_ERROR_CODE.TOTAL_TASK_LIMIT_REACHED
          ? t('limitModal.employerTotalTasksHint')
          : payload.code === LIMIT_ERROR_CODE.PIN_MESSAGE_LIMIT_REACHED ||
              payload.code === LIMIT_ERROR_CODE.PIN_MESSAGE_ACTIVE_LIMIT_REACHED
            ? t('limitModal.employerPinMessagesHint')
            : t('limitModal.employerDailyHint');

  const showSubscriptionCta = !isHelper && !isPaidEmployer && canUseStripeBillingUi();
  const showPlayStoreBillingHint =
    !isHelper && !isPaidEmployer && isPlayStoreClient() && showPlayStoreOfficialWebsiteUrl;

  return (
    <AppModal
      open
      onClose={onClose}
      title={title}
      titleId="limit-reached-modal-title"
      closeAriaLabel={t('common.cancel')}
      backdropZClass="z-[70]"
      panelMaxWidthClass="max-w-sm"
    >
      <div className="space-y-2">
        {bodyLines.map((line, index) => (
          <p key={index} className="text-sm text-gray-700 dark:text-gray-200">
            {line}
          </p>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      {showPlayStoreBillingHint ? (
        <p className="mt-2 text-xs text-amber-800 dark:text-amber-200" role="status">
          {t('limitModal.playStoreBillingHint')}
        </p>
      ) : null}
      <div className="mt-4 flex flex-col gap-2">
        {showSubscriptionCta ? (
          <AppButton
            type="button"
            variant="primary"
            fullWidth
            onClick={onGoSubscription}
            onKeyDown={onGoSubscriptionKeyDown}
            tabIndex={0}
            aria-label={t('limitModal.goSubscription')}
          >
            {t('limitModal.goSubscription')}
          </AppButton>
        ) : null}
        <AppButton
          type="button"
          variant={
            isHelper || isPaidEmployer || !showSubscriptionCta ? 'primary' : 'secondary'
          }
          fullWidth
          onClick={onViewUsage}
          onKeyDown={onViewUsageKeyDown}
          tabIndex={0}
          aria-label={t('limitModal.viewUsage')}
        >
          {t('limitModal.viewUsage')}
        </AppButton>
      </div>
    </AppModal>
  );
};

type LimitReachedModalContextValue = {
  showFromError: (err: unknown) => boolean;
};

const LimitReachedModalContext = createContext<LimitReachedModalContextValue | null>(null);

export const LimitReachedModalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [payload, setPayload] = useState<LimitReachedPayload | null>(null);
  const { playStoreShowOfficialWebsiteUrl } = usePublicConfig(isPlayStoreClient());

  const isHelper = user?.role === USER_ROLE.HELPER;
  const isPaidEmployer =
    user?.role === USER_ROLE.EMPLOYER && user.level === USER_LEVEL.PAID;

  const handleClose = useCallback(() => {
    setPayload(null);
  }, []);

  const handleViewUsage = useCallback(() => {
    setPayload(null);
    navigate('/settings/usage');
  }, [navigate]);

  const handleGoSubscription = useCallback(() => {
    setPayload(null);
    navigate('/settings/subscription');
  }, [navigate]);

  const handleViewUsageKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleViewUsage();
    }
  };

  const handleGoSubscriptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleGoSubscription();
    }
  };

  useEffect(() => {
    registerLimitReachedListener((next) => {
      setPayload(next);
    });
    return () => {
      registerLimitReachedListener(null);
    };
  }, []);

  const showFromError = useCallback((err: unknown): boolean => {
    const parsed = parseLimitReachedFromApiClientError(err);
    if (!parsed) {
      return false;
    }
    emitLimitReached(parsed);
    return true;
  }, []);

  const contextValue = useMemo(
    () => ({
      showFromError,
    }),
    [showFromError],
  );

  return (
    <LimitReachedModalContext.Provider value={contextValue}>
      {children}
      {payload ? (
        <LimitReachedModalView
          payload={payload}
          isHelper={isHelper}
          isPaidEmployer={isPaidEmployer}
          showPlayStoreOfficialWebsiteUrl={playStoreShowOfficialWebsiteUrl}
          t={t}
          onClose={handleClose}
          onViewUsage={handleViewUsage}
          onViewUsageKeyDown={handleViewUsageKeyDown}
          onGoSubscription={handleGoSubscription}
          onGoSubscriptionKeyDown={handleGoSubscriptionKeyDown}
        />
      ) : null}
    </LimitReachedModalContext.Provider>
  );
};

export const useLimitReachedModal = (): LimitReachedModalContextValue => {
  const ctx = useContext(LimitReachedModalContext);
  if (!ctx) {
    throw new Error('useLimitReachedModal must be used within LimitReachedModalProvider');
  }
  return ctx;
};
